import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Row, Col, NavItem, Table as BootstrapTable } from 'react-bootstrap';
import moment from 'moment';
import dayjs from 'dayjs';

import { 
  Container, 
  Button, 
  Box, 
  Card, 
  IconButton, 
  MenuItem, 
  Popover, 
  Stack, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TablePagination, 
  TableRow, 
  Typography, 
  Tab, 
  FormControl, 
  Grid, 
  TextField, 
  CardContent, 
  Autocomplete, 
  Divider,
  Fab,
  InputLabel,
  Select
} from '@mui/material';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { DataGrid } from '@mui/x-data-grid';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { KeyboardArrowLeftRounded } from '@mui/icons-material';

import { getLocalStorageItem } from '../../../common/localStorageUtils';
import { authKey, GET_APPROVED_STAKEHOLDERS, GET_CURRENT_INVENTORY_LIST, GET_DASHBOARD_INVENTORY_LIST } from '../../../common/URLs';
import { toast } from 'react-toastify';
import apiClient from '../../../axios';
import { loaderService } from '../../../common/custom-loader/loader-service';
import BadgeComponent from '../../../common/badge';
import { urnMaxLength } from '../../../common/commonValidations';
import { CloseIcon, SearchIcon } from '../../../common/icons';
import CustomDataGridToolbar from '../../../common/customDataGridToolbar';
import { QuickSearchToolbar } from '../../../payment/common/commonFunctions';

function StakeholderPopUp() {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [tabValue, setTabValue] = useState(location?.state?.materialType);
  const [inventoryDetails, setInventoryDetails] = useState([]);
  const [inventorySummary, setInventorySummary] = useState();
  const [urnList, setUrnList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  const [searchFilter, setSearchFilter] = useState({
    fromDate: null,
    toDate: null,
  });

  useEffect(() => {
    if (getLocalStorageItem('id_token')) {
      getStakeholderDetails();
    }
  }, [searchFilter, searchTerm]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const getStakeholderDetails = (fromDate, toDate, urn) => {
    loaderService.showLoader();
    apiClient
      .post(GET_CURRENT_INVENTORY_LIST,
        {
          "stakeHolderType": location?.state?.stakeHolderType,
          "materialType": location?.state?.materialType,
          "inventoryType": location?.state?.materialType === "ChipBamboo" ? "chipping" : '',
          "category": location?.state?.inventoryCategory,
          "fromDate": fromDate ? fromDate : searchFilter?.fromDate ? searchFilter?.fromDate : null,
          "toDate": toDate ? toDate : searchFilter?.toDate ? searchFilter?.toDate : null,
          "stakeHolderUrn": urn ? urn : searchTerm ? searchTerm : "",
        },
        {
          headers: {
            Authorization: 'Bearer ' + getLocalStorageItem('id_token'),
          }
        }, null
      )
      .then((response) => {
        setInventoryDetails(response?.data?.inventoryList);
        setInventorySummary(response?.data?.inventorySummary);
        loaderService.hideLoader();
      })
      .catch(function (err) {
        toast.error(err.message);
        loaderService.hideLoader();
      });
  };

  const parseDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;

    const isoDate = new Date(value);
    if (!isNaN(isoDate.getTime())) return isoDate;

    const parts = value.split(/[/-]/);
    if (parts.length === 3) {
      const [dd, mm, yyyy] = parts;
      return new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    }
    return null;
  };

  const columns = [
    { field: 'bundleId', headerName: 'Heap', width: 200 },
    { field: 'stakeholderType', headerName: 'Stakeholder Type', width: 140 },
    { field: 'stakeholderUrn', headerName: 'Stakeholder URN', width: 140 },
    {
      field: 'isUnrestrictedInventory', headerName: 'Un-Restricted', width: 100,
      valueGetter: (params) => params.value?.isUnrestrictedInventory === true ? "Yes" : "No",
      renderCell: (params) => params.row.isUnrestrictedInventory === true ? "Yes" : "No"
    },
    {
      field: 'isRestrictedInventory', headerName: 'Restricted', width: 100,
      valueGetter: (params) => params.value?.isRestrictedInventory === true ? "Yes" : "No",
      renderCell: (params) => params.row.isRestrictedInventory === true ? "Yes" : "No"
    },
    {
      field: 'isScrapInventory', headerName: 'Scrap', width: 100,
      valueGetter: (params) => params.value?.isScrapInventory === true ? "Yes" : "No",
      renderCell: (params) => params.row.isScrapInventory === true ? "Yes" : "No"
    },
    {
      field: 'grnDate', headerName: "GRN Date", width: 120, type: "date",
      valueGetter: (params) => parseDate(params.row.grnDate),
      valueFormatter: params => moment(params?.value).format("DD/MM/YYYY"),
    },
    { field: 'arrivalWeight', headerName: 'Arrival Weight(MT)', width: 150, type: "number" },
    { field: 'ageingDays', headerName: 'Ageing Days', width: 140, type: "number" },
    { field: 'weightLossPercent', headerName: 'Weight Loss Percent', width: 150, type: "number" },
    { field: 'currentWeight', headerName: 'Current Weight(MT)', width: 150, type: "number" },
  ];

  const handleDateChange = (name, value) => {
    const formatted = value ? value.format("YYYY-MM-DD") : null;

    setSearchFilter((prev) => {
      if (name === "toDate" && prev.fromDate && dayjs(prev.fromDate).isAfter(value)) {
        return { ...prev, toDate: formatted, fromDate: formatted };
      }
      return { ...prev, [name]: formatted };
    });
  };

  function clearSearch(e) {
    e.preventDefault();
    setSearchFilter({ fromDate: null, toDate: null });
    setSearchTerm('');
  }

  useEffect(() => {
    if (location?.state?.stakeHolderType) {
      stakeholderList();
    }
  }, [location?.state?.stakeHolderType]);

  const stakeholderList = () => {
    loaderService.showLoader();
    apiClient.post(GET_APPROVED_STAKEHOLDERS,
      { "type": location?.state?.stakeHolderType },
      {
        headers: {
          Authorization: 'Bearer ' + getLocalStorageItem('id_token'),
          "Content-Type": "application/json"
        }
      })
      .then((response) => {
        setUrnList(response.data.stakeHolders);
        loaderService.hideLoader();
      }).catch(function (error) {
        loaderService.hideLoader();
        toast.error(error.message);
      });
  };

  const urns = location?.state?.stakeHolderType === "Plant"
    ? [{ urn: "ABEPL", name: "ABEPL" }]
    : urnList;

  const handleSearch = (query) => {
    const q = String(query).toLowerCase();
    const results = urns?.filter(item => {
      const urn = String(item.urn || "");
      const name = String(item.name || "");
      return urn.toLowerCase().includes(q) || name.toLowerCase().includes(q);
    }) || [];
    setSearchResults(results);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 3 }}>
      <Box display="flex" alignItems="center" mb={3}>
        <Fab
          onClick={() => { navigate("/inventoryDashboard", { state: { "materialType": tabValue } }); }}
          size="small"
          aria-label="back"
          title="Back"
          color="default"
          sx={{ mr: 2, boxShadow: 1 }}
        >
          <KeyboardArrowLeftRounded />
        </Fab>
        <Typography variant="h5" fontWeight={600}>
          Stakeholder Inventory Details
        </Typography>
      </Box>

      <Card elevation={2} sx={{ borderRadius: 2 }}>
        <CardContent>
          
          <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DesktopDatePicker
                    label="From Date"
                    value={searchFilter?.fromDate ? dayjs(searchFilter.fromDate) : null}
                    inputFormat="DD/MM/YYYY"
                    maxDate={searchFilter?.toDate ? dayjs(searchFilter.toDate) : dayjs()}
                    onChange={(value) => handleDateChange("fromDate", value)}
                    renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                  />
                </LocalizationProvider>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DesktopDatePicker
                    label="To Date"
                    value={searchFilter?.toDate ? dayjs(searchFilter.toDate) : null}
                    inputFormat="DD/MM/YYYY"
                    maxDate={dayjs()}
                    onChange={(value) => handleDateChange("toDate", value)}
                    renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                  />
                </LocalizationProvider>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={8} md>
              <Autocomplete
                value={urns.find(o => o.urn === searchTerm) || null}
                getOptionLabel={(opt) => `${opt.urn} - ${opt.name}`}
                isOptionEqualToValue={(opt, val) => opt.urn === val.urn}
                onChange={(_, newValue) => {
                  const urn = newValue?.urn ?? "";
                  setSearchTerm(urn);
                  handleSearch(newValue);
                }}
                options={urns || []}
                renderInput={(params) => (
                  <TextField {...params} label="Search URN" variant="outlined" fullWidth size="small" required />
                )}
              />
            </Grid>
            
            <Grid item xs={12} sm={4} md="auto" display="flex" alignItems="center">
              <Button 
                startIcon={<CloseIcon />} 
                variant="outlined" 
                color="error" 
                onClick={(e) => clearSearch(e)}
                sx={{ height: '40px', px: 3, width: { xs: '100%', md: 'auto' } }}
              >
                Clear Search
              </Button>
            </Grid>
          </Grid>

          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={0} sx={{ borderRadius: 2, border: "1px solid #e0e0e0", backgroundColor: "#fafafa" }}>
                <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Total Arrival Weight
                  </Typography>
                  <Typography variant="h6" fontWeight={600} color="primary.main">
                    {inventorySummary?.arrivalWeightSum || 0} MT
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={0} sx={{ borderRadius: 2, border: "1px solid #e0e0e0", backgroundColor: "#fafafa" }}>
                <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Total Current Weight
                  </Typography>
                  <Typography variant="h6" fontWeight={700} color="primary.main">
                    {inventorySummary?.currentWeightSum || 0} MT
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <TabContext value={tabValue}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <TabList onChange={handleTabChange} variant="scrollable">
                {location?.state?.materialType === 'RawBamboo' && <Tab label="Raw Bamboo" value="RawBamboo" />}
                {location?.state?.materialType === 'ChipBamboo' && <Tab label="Chipped Bamboo" value="ChipBamboo" />}
              </TabList>
            </Box>
            
            <TabPanel value="RawBamboo" sx={{ px: 0, pt: 2 }}>
              <Box style={{ height: 450, width: '100%' }}>
                {inventoryDetails && (
                  <DataGrid 
                    rows={inventoryDetails} 
                    columns={columns}
                    components={{ Toolbar: CustomDataGridToolbar }}
                    componentsProps={{
                      toolbar: {
                        rows: inventoryDetails,
                        columns: columns,
                        fileName: "RB_Inventory.xlsx",
                        isExportToExcelRequired: true,
                        dateFields: ['grnDate']
                      }
                    }}
                    getRowId={(row) => row?.bundleId} 
                  />
                )}
              </Box>
            </TabPanel>

            <TabPanel value="ChipBamboo" sx={{ px: 0, pt: 2 }}>
              <Box style={{ height: 450, width: '100%' }}>
                <DataGrid 
                  rows={inventoryDetails || []} 
                  columns={columns} 
                  components={{ Toolbar: CustomDataGridToolbar }}
                  componentsProps={{
                    toolbar: {
                      rows: inventoryDetails,
                      columns: columns,
                      fileName: "CB_Inventory.xlsx",
                      isExportToExcelRequired: true,
                      dateFields: ['grnDate']
                    }
                  }}
                  getRowId={(row) => row?.bundleId} 
                />
              </Box>
            </TabPanel>
          </TabContext>
          
        </CardContent>
      </Card>
    </Container>
  );
}

export default StakeholderPopUp;
